import { TFunction } from 'react-i18next';
import {
  KPI_METRIC_TYPE,
  KPI_RANGE,
  KpiBadgeLevel,
  KpiCustomThresholds,
  KpiMetricType,
  KpiScaleLevel,
  KpiState,
  NPS_THRESHOLDS,
  PERCENTAGE_THRESHOLDS,
  SCALE_LEVEL_STATE,
  SCALE_LEVEL_THRESHOLDS,
} from './kpiTypes';

export type KpiEvaluation = {
  /** Colour state for the bar fill and the score. */
  state: KpiState;
  /** Badge interpretation level — 5-level for `scale`, 2-level (good/needsAttention) for every other metric type. */
  badgeLevel: KpiScaleLevel | KpiBadgeLevel;
  /** Translated badge text, ready to render. */
  badgeText: string;
  /** Position of the value within the metric range, 0–1. */
  ratio: number;
  /** Lower bound of the metric range. */
  min: number;
  /** Upper bound of the metric range. */
  max: number;
};

export type KpiEvaluateOptions = {
  /** Overrides the metric's lower range bound. Only meaningful for `custom` — other metric types use their fixed `KPI_RANGE`. */
  min?: number;
  /** Overrides the metric's upper range bound. Only meaningful for `custom`. */
  max?: number;
  /** Success/warning thresholds for the `custom` metric. Without it, `custom` always evaluates to `normal`. */
  customThresholds?: KpiCustomThresholds;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** Resolves the scale interpretation level (excellent → inadequate) for a 0–4 value. */
export const evaluateScaleLevel = (value: number): KpiScaleLevel => {
  const clamped = clamp(value, KPI_RANGE[KPI_METRIC_TYPE.SCALE].min, KPI_RANGE[KPI_METRIC_TYPE.SCALE].max);
  const match = SCALE_LEVEL_THRESHOLDS.find((threshold) => clamped >= threshold.from);
  return match?.level ?? 'inadequate';
};

/**
 * Resolves the badge interpretation level for a metric: the `scale` metric gets its
 * 5-level breakdown, every other metric type collapses its colour state into just
 * `good` (success) or `needsAttention` (warning/normal/danger).
 */
export const evaluateBadgeLevel = (
  value: number,
  metricType: KpiMetricType,
  state: KpiState,
): KpiScaleLevel | KpiBadgeLevel => {
  if (metricType === KPI_METRIC_TYPE.SCALE) {
    return evaluateScaleLevel(value);
  }
  return state === 'success' ? 'good' : 'needsAttention';
};

/** Resolves the colour state for every supported KPI metric type. */
export const evaluateMetricState = (
  value: number,
  metricType: KpiMetricType,
  options?: {
    min?: number;
    max?: number;
    customThresholds?: KpiCustomThresholds;
  },
): KpiState => {
  const isCustom = metricType === KPI_METRIC_TYPE.CUSTOM;
  const min = isCustom ? options?.min ?? KPI_RANGE[metricType].min : KPI_RANGE[metricType].min;
  const max = isCustom ? options?.max ?? KPI_RANGE[metricType].max : KPI_RANGE[metricType].max;
  const clamped = clamp(value, min, max);

  if (metricType === KPI_METRIC_TYPE.SCALE) {
    return SCALE_LEVEL_STATE[evaluateScaleLevel(value)];
  }

  if (metricType === KPI_METRIC_TYPE.NPS) {
    return evaluateNpsState(clamped);
  }

  if (metricType === KPI_METRIC_TYPE.PERCENTAGE) {
    return evaluatePercentageState(clamped);
  }

  if (isCustom && options?.customThresholds) {
    return evaluateCustomState(value, options.customThresholds, min, max);
  }

  return 'normal';
};

/** Resolves the NPS score colour state from its thresholds. */
export const evaluateNpsState = (value: number): KpiState => {
  const clamped = clamp(value, KPI_RANGE[KPI_METRIC_TYPE.NPS].min, KPI_RANGE[KPI_METRIC_TYPE.NPS].max);
  if (clamped >= NPS_THRESHOLDS.successFrom) {
    return 'success';
  }
  if (clamped <= NPS_THRESHOLDS.warningFrom) {
    return 'warning';
  }
  return 'warning';
};

/** Resolves the percentage score colour state from its thresholds. */
export const evaluatePercentageState = (value: number): KpiState => {
  const clamped = clamp(value, KPI_RANGE[KPI_METRIC_TYPE.PERCENTAGE].min, KPI_RANGE[KPI_METRIC_TYPE.PERCENTAGE].max);
  if (clamped >= PERCENTAGE_THRESHOLDS.successFrom) {
    return 'success';
  }
  if (clamped <= PERCENTAGE_THRESHOLDS.warningFrom) {
    return 'warning';
  }
  return 'warning';
};

/** Resolves the `custom` score colour state from caller-supplied thresholds. */
export const evaluateCustomState = (
  value: number,
  thresholds: KpiCustomThresholds,
  min: number,
  max: number,
): KpiState => {
  const clamped = clamp(value, min, max);
  if (clamped >= thresholds.successFrom) {
    return 'success';
  }
  if (clamped >= thresholds.warningFrom) {
    return 'warning';
  }
  return 'warning';
};

/**
 * Evaluates a KPI value for a given metric type, returning the colour state,
 * the scale level (scale only) and the normalized position within the range.
 *
 * For `custom`, pass `options.customThresholds` (and optionally `options.min`/`max`
 * to override the default 0–100 range) — the same way `nps` has its own fixed thresholds.
 */
export const evaluateKpi = (
  value: number,
  metricType: KpiMetricType,
  t: TFunction,
  options?: KpiEvaluateOptions,
): KpiEvaluation => {
  const isCustom = metricType === KPI_METRIC_TYPE.CUSTOM;
  const min = isCustom ? options?.min ?? KPI_RANGE[metricType].min : KPI_RANGE[metricType].min;
  const max = isCustom ? options?.max ?? KPI_RANGE[metricType].max : KPI_RANGE[metricType].max;
  const clamped = clamp(value, min, max);
  const span = max - min;
  const ratio = span === 0 ? 0 : (clamped - min) / span;

  const state = evaluateMetricState(value, metricType, options);
  const badgeLevel = evaluateBadgeLevel(value, metricType, state);

  return {
    state,
    badgeLevel,
    badgeText: t(badgeLevel),
    ratio,
    min,
    max,
  };
};
